import { useState, useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getExpandedRowModel,
  flexRender,
  createColumnHelper,
  type SortingState,
  type Row,
  type ColumnFiltersState,
} from "@tanstack/react-table";
import { useAdminData, type AdminOrder } from "./AdminDataContext";
import { useAdminAuth } from "./AdminAuthContext";
import { AdminEmailNotConfiguredNotice } from "./AdminEmailNotConfiguredNotice";
import { Search, ChevronDown, ChevronRight, Eye, X, Trash2 } from "lucide-react";

const statusColors: Record<AdminOrder["status"], { bg: string; text: string }> = {
  pending: { bg: "#C7A76C15", text: "#C7A76C" },
  confirmed: { bg: "#6F7F6315", text: "#6F7F63" },
  shipped: { bg: "#8A6D6315", text: "#8A6D63" },
  completed: { bg: "#74856D15", text: "#74856D" },
  cancelled: { bg: "#8B5C5815", text: "#8B5C58" },
};

const STATUS_OPTIONS: { value: AdminOrder["status"]; label: string }[] = [
  { value: "pending", label: "Pending" },
  { value: "confirmed", label: "Confirmed" },
  { value: "shipped", label: "Shipped" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

const columnHelper = createColumnHelper<AdminOrder>();

export function AdminOrders() {
  const { orders, updateOrderStatus, updateOrderStatusBulk, hideOrders } = useAdminData();
  const { hasPermission } = useAdminAuth();
  const [viewingOrder, setViewingOrder] = useState<AdminOrder | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [sorting, setSorting] = useState<SortingState>([{ id: "created_at", desc: true }]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [grouping, setGrouping] = useState<string[]>([]);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  if (!hasPermission("orders")) {
    return (
      <p className="text-[#6F675F]" style={{ fontSize: "14px" }}>
        You don&apos;t have access to this section.
      </p>
    );
  }

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "select",
        header: ({ table }) => (
          <input
            type="checkbox"
            checked={table.getIsAllPageRowsSelected()}
            indeterminate={table.getIsSomePageRowsSelected()}
            onChange={(e) => table.toggleAllPageRowsSelected(e.target.checked)}
            className="rounded border-[#E6E0D7] text-[#6F7F63]"
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onChange={(e) => row.toggleSelected(e.target.checked)}
            onClick={(e) => e.stopPropagation()}
            className="rounded border-[#E6E0D7] text-[#6F7F63]"
          />
        ),
        size: 40,
      }),
      columnHelper.accessor("order_number", {
        header: "Order",
        cell: (info) => info.getValue(),
        enableSorting: true,
        enableGrouping: false,
      }),
      columnHelper.accessor("customer_name", {
        header: "Customer",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div>
              <p className="text-[#2D2A26]" style={{ fontSize: "13px" }}>{row.customer_name}</p>
              <p className="text-[#D8D1C7]" style={{ fontSize: "11px" }}>{row.customer_email}</p>
            </div>
          );
        },
        enableSorting: true,
        enableGrouping: true,
      }),
      columnHelper.accessor("subtotal", {
        header: () => <span className="text-right block">Total</span>,
        cell: (info) => (
          <span className="text-right block text-[#2D2A26]" style={{ fontSize: "13px" }}>
            €{info.getValue()}
          </span>
        ),
        enableSorting: true,
      }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: (info) => {
          const order = info.row.original;
          const colors = statusColors[order.status];
          return (
            <select
              value={order.status}
              onChange={async (e) => {
                setStatusError(null);
                const result = await updateOrderStatus(order.id, e.target.value as AdminOrder["status"]);
                if (result.error) setStatusError(result.error);
              }}
              onClick={(e) => e.stopPropagation()}
              className="appearance-none px-2.5 py-1 rounded-full border-0 cursor-pointer text-center"
              style={{
                fontSize: "10px",
                fontWeight: 400,
                background: colors.bg,
                color: colors.text,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
              }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          );
        },
        enableSorting: true,
        enableGrouping: true,
      }),
      columnHelper.accessor("created_at", {
        header: "Date",
        cell: (info) => (
          <span className="text-[#D8D1C7]" style={{ fontSize: "12px" }}>
            {new Date(info.getValue()).toLocaleDateString()}
          </span>
        ),
        enableSorting: true,
      }),
      columnHelper.display({
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-1">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setViewingOrder(row.original); }}
              className="p-1.5 text-[#6F675F] hover:text-[#6F7F63] transition-colors"
              title="View"
            >
              <Eye size={14} strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={async (e) => {
                e.stopPropagation();
                if (window.confirm("Remove this order from the list? It will stay in the database as cancelled.")) {
                  setStatusError(null);
                  const result = await hideOrders([row.original.id]);
                  if (result.error) setStatusError(result.error);
                }
              }}
              className="p-1.5 text-[#6F675F] hover:text-[#8B5C58] transition-colors"
              title="Remove from list"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ),
        size: 80,
      }),
    ],
    [updateOrderStatus, hideOrders]
  );

  const table = useReactTable({
    data: orders,
    columns,
    state: {
      sorting,
      globalFilter,
      columnFilters,
      grouping,
      rowSelection,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onGroupingChange: setGrouping,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: (row) => !!row.subRows?.length,
    defaultExpanded: true,
    enableRowSelection: (row) => !row.getIsGrouped(),
    globalFilterFn: (row, _columnId, filterValue) => {
      const q = String(filterValue ?? "").toLowerCase().trim();
      if (!q) return true;
      const o = row.original;
      return (
        o.order_number.toLowerCase().includes(q) ||
        o.customer_name.toLowerCase().includes(q) ||
        o.customer_email.toLowerCase().includes(q)
      );
    },
  });

  const selectedRows = table.getFilteredSelectedRowModel().rows;
  const selectedIds = selectedRows.map((r) => r.original.id);

  const handleBulkStatus = async (status: AdminOrder["status"]) => {
    if (selectedIds.length === 0) return;
    setStatusError(null);
    const result = await updateOrderStatusBulk(selectedIds, status);
    if (result.error) setStatusError(result.error);
    else setRowSelection({});
  };

  const handleBulkHide = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Remove ${selectedIds.length} order(s) from the list? They will remain in the database as cancelled.`)) return;
    setStatusError(null);
    const result = await hideOrders(selectedIds);
    if (result.error) setStatusError(result.error);
    else setRowSelection({});
  };

  return (
    <div>
      <h1
        className="text-[#2D2A26] tracking-[0.08em] mb-8"
        style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "28px", fontWeight: 400 }}
      >
        Orders
      </h1>
      <AdminEmailNotConfiguredNotice />
      {statusError && (
        <p className="text-[#8B5C58] mb-4" style={{ fontSize: "12px" }}>{statusError}</p>
      )}

      {orders.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-[0_2px_16px_rgba(40,32,20,0.04)] text-center">
          <p className="text-[#D8D1C7]" style={{ fontSize: "14px" }}>
            No orders yet. Orders from the checkout will appear here.
          </p>
        </div>
      ) : (
        <>
          {/* Toolbar: search, filters, group by, bulk actions */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D8D1C7]" />
              <input
                type="text"
                value={globalFilter ?? ""}
                onChange={(e) => setGlobalFilter(e.target.value)}
                placeholder="Search orders..."
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-[#E6E0D7] rounded-lg text-[#2D2A26] placeholder:text-[#D8D1C7] focus:outline-none focus:border-[#C7A76C]"
                style={{ fontSize: "13px", fontWeight: 300 }}
              />
            </div>
            <select
              value={(columnFilters.find((f) => f.id === "status")?.value as string) ?? "all"}
              onChange={(e) => {
                const v = e.target.value;
                setColumnFilters((prev) =>
                  v === "all" ? prev.filter((f) => f.id !== "status") : [...prev.filter((f) => f.id !== "status"), { id: "status", value: v }]
                );
              }}
              className="px-4 py-2.5 bg-white border border-[#E6E0D7] rounded-lg text-[#2D2A26] focus:outline-none focus:border-[#C7A76C] cursor-pointer"
              style={{ fontSize: "13px", fontWeight: 300 }}
            >
              <option value="all">All statuses</option>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <select
              value={grouping[0] ?? ""}
              onChange={(e) => setGrouping(e.target.value ? [e.target.value] : [])}
              className="px-4 py-2.5 bg-white border border-[#E6E0D7] rounded-lg text-[#2D2A26] focus:outline-none focus:border-[#C7A76C] cursor-pointer"
              style={{ fontSize: "13px", fontWeight: 300 }}
            >
              <option value="">No grouping</option>
              <option value="status">Group by status</option>
              <option value="customer_name">Group by customer</option>
            </select>
            {selectedIds.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 ml-auto">
                <span className="text-[#6F675F]" style={{ fontSize: "12px" }}>
                  {selectedIds.length} selected
                </span>
                <select
                  onChange={(e) => {
                    const v = e.target.value as AdminOrder["status"] | "";
                    if (v) handleBulkStatus(v);
                    e.target.value = "";
                  }}
                  className="px-3 py-2 bg-white border border-[#E6E0D7] rounded-lg text-[#2D2A26] cursor-pointer"
                  style={{ fontSize: "12px" }}
                >
                  <option value="">Set status…</option>
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handleBulkHide}
                  className="flex items-center gap-1.5 px-3 py-2 text-[#6F675F] hover:text-[#8B5C58] border border-[#E6E0D7] rounded-lg transition-colors"
                  style={{ fontSize: "12px" }}
                >
                  <Trash2 size={12} /> Remove from list
                </button>
              </div>
            )}
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl shadow-[0_2px_16px_rgba(40,32,20,0.04)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  {table.getHeaderGroups().map((hg) => (
                    <tr key={hg.id} className="border-b border-[#E6E0D7]">
                      {hg.headers.map((h) => (
                        <th
                          key={h.id}
                          className="text-left px-5 py-3 text-[#6F675F] tracking-[0.1em] uppercase whitespace-nowrap"
                          style={{ fontSize: "10px", fontWeight: 400, width: h.getSize() }}
                        >
                          <div className="flex items-center gap-1">
                            {h.column.getCanGroup() ? (
                              <button
                                type="button"
                                onClick={() => h.column.toggleGrouping()}
                                className="p-0.5 hover:bg-[#F2EEE8] rounded"
                              >
                                {h.column.getIsGrouped() ? (
                                  <ChevronDown size={12} />
                                ) : (
                                  <ChevronRight size={12} />
                                )}
                              </button>
                            ) : null}
                            <span
                              className={h.column.getCanSort() ? "cursor-pointer select-none hover:text-[#2D2A26]" : ""}
                              onClick={() => h.column.toggleSorting(h.column.getIsSorted() === "asc")}
                            >
                              {flexRender(h.column.columnDef.header, h.getContext())}
                            </span>
                            {h.column.getIsSorted() && (
                              <span className="text-[#C7A76C]">
                                {h.column.getIsSorted() === "asc" ? " ↑" : " ↓"}
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id} row={row} table={table} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Order detail modal */}
      {viewingOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm px-4">
          <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E6E0D7]">
              <h2
                className="text-[#2D2A26]"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 400 }}
              >
                {viewingOrder.order_number}
              </h2>
              <button
                type="button"
                onClick={() => setViewingOrder(null)}
                className="text-[#D8D1C7] hover:text-[#2D2A26] transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[#6F675F] tracking-[0.1em] uppercase" style={{ fontSize: "10px" }}>Customer</p>
                  <p className="text-[#2D2A26]" style={{ fontSize: "13px" }}>{viewingOrder.customer_name}</p>
                  <p className="text-[#D8D1C7]" style={{ fontSize: "12px" }}>{viewingOrder.customer_email}</p>
                </div>
                <div>
                  <p className="text-[#6F675F] tracking-[0.1em] uppercase" style={{ fontSize: "10px" }}>Delivery</p>
                  <p className="text-[#2D2A26] capitalize" style={{ fontSize: "13px" }}>{viewingOrder.delivery}</p>
                  {viewingOrder.shipping_address && (
                    <p className="text-[#D8D1C7]" style={{ fontSize: "12px" }}>{viewingOrder.shipping_address}</p>
                  )}
                </div>
              </div>
              {viewingOrder.notes && (
                <div>
                  <p className="text-[#6F675F] tracking-[0.1em] uppercase mb-1" style={{ fontSize: "10px" }}>Notes</p>
                  <p className="text-[#2D2A26]" style={{ fontSize: "13px", fontWeight: 300 }}>{viewingOrder.notes}</p>
                </div>
              )}
              <div className="border-t border-[#E6E0D7] pt-4">
                <p className="text-[#6F675F] tracking-[0.1em] uppercase mb-3" style={{ fontSize: "10px" }}>Items</p>
                <div className="space-y-2">
                  {viewingOrder.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between">
                      <span className="text-[#2D2A26]" style={{ fontSize: "13px" }}>
                        {item.quantity} × {item.product_name}
                      </span>
                      <span className="text-[#2D2A26]" style={{ fontSize: "13px" }}>
                        €{item.unit_price * item.quantity}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between mt-3 pt-3 border-t border-[#E6E0D7]">
                  <span className="text-[#6F675F] tracking-[0.1em] uppercase" style={{ fontSize: "11px" }}>Total</span>
                  <span
                    className="text-[#2D2A26]"
                    style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px" }}
                  >
                    €{viewingOrder.subtotal}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TableRow({
  row,
  table,
}: {
  row: Row<AdminOrder>;
  table: ReturnType<typeof useReactTable<AdminOrder>>;
}) {
  const isGrouped = row.getIsGrouped();
  const canExpand = row.getCanExpand();
  const groupingColId = isGrouped ? (row as Row<AdminOrder> & { groupingColumnId?: string }).groupingColumnId : undefined;

  return (
    <>
      <tr
        className="border-b border-[#E6E0D7]/50 hover:bg-[#F2EEE8]/50 transition-colors"
        onClick={canExpand ? () => row.toggleExpanded() : undefined}
        style={{ cursor: canExpand ? "pointer" : undefined }}
      >
        {row.getVisibleCells().map((cell) => {
          const cellIsGrouped = typeof (cell as { getIsGrouped?: () => boolean }).getIsGrouped === "function"
            && (cell as { getIsGrouped: () => boolean }).getIsGrouped();
          const isPlaceholder = typeof (cell as { getIsPlaceholder?: () => boolean }).getIsPlaceholder === "function"
            && (cell as { getIsPlaceholder: () => boolean }).getIsPlaceholder();
          return (
            <td
              key={cell.id}
              className="px-5 py-3"
              style={{
                paddingLeft: isGrouped && (cell.column.id === groupingColId) ? `${row.depth * 20 + 20}px` : undefined,
                fontSize: "13px",
              }}
            >
              {isGrouped && cellIsGrouped ? (
                <span className="flex items-center gap-1">
                  {row.getIsExpanded() ? (
                    <ChevronDown size={14} className="text-[#6F675F]" />
                  ) : (
                    <ChevronRight size={14} className="text-[#6F675F]" />
                  )}
                  <span className="font-medium text-[#2D2A26]">
                    {groupingColId === "status" && `Status: ${row.getValue("status")}`}
                    {groupingColId === "customer_name" && `Customer: ${row.original.customer_email}`}
                  </span>
                  {row.subRows?.length != null && ` (${row.subRows.length})`}
                </span>
              ) : isPlaceholder ? null : (
                flexRender(cell.column.columnDef.cell, cell.getContext())
              )}
            </td>
          );
        })}
      </tr>
    </>
  );
}
